export const API_ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  SERVER: 'SERVER_ERROR',
  CLIENT: 'CLIENT_ERROR',
} as const;

export type ApiErrorType = (typeof API_ERROR_TYPES)[keyof typeof API_ERROR_TYPES];

export interface ApiResponseEnvelope<T> {
  resp?: T;
  token?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

export interface ApiErrorDetails {
  status?: number;
  body?: ApiResponseEnvelope<unknown>;
  response?: Response;
  path?: string;
  originalError?: unknown;
}

export interface ApiError extends Error {
  type: ApiErrorType;
  isApiError: true;
  status?: number;
  body?: ApiResponseEnvelope<unknown>;
  response?: Response;
  path?: string;
  originalError?: unknown;
}

export const isApiError = (value: unknown): value is ApiError =>
  value instanceof Error && (value as Partial<ApiError>).isApiError === true;

const createApiError = (
  type: ApiErrorType,
  message: string,
  details: ApiErrorDetails = {},
): ApiError => {
  const error = new Error(message) as ApiError;
  error.type = type;
  error.isApiError = true;
  Object.assign(error, details);
  return error;
};

export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

export interface RequestContext {
  path: string;
  data: unknown;
  options: RequestOptions;
}

export type ResponseCallback = (
  respBody: ApiResponseEnvelope<unknown>,
  respHeaders: Record<string, string>,
  request: RequestContext,
) => void;

export interface ApiClientConfig {
  apiURL: string;
  getToken: () => string | null;
  getLocale: () => string;
  onResponse?: ResponseCallback;
}

export interface ApiClient {
  get<T = unknown>(path: string, options?: RequestOptions): Promise<T>;
  post<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  put<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  patch<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  delete<T = unknown>(path: string, data?: unknown, options?: RequestOptions): Promise<T>;
  /** GET returning the raw response body (CSV, images). Throws ApiError on non-2xx; onResponse callbacks are not invoked (no JSON envelope). */
  getBlob(path: string, options?: RequestOptions): Promise<Blob>;
  /** POST with a JSON body, returning the raw response body. Same error semantics as getBlob. */
  postBlob(path: string, data?: unknown, options?: RequestOptions): Promise<Blob>;
}

const baseHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// `path` may already carry a hand-built query string (e.g. `/x?position=0`),
// so join with `&` in that case — a second `?` would corrupt the last
// hand-built param's value on the Rails side (`position=0?locale=hu`).
const buildQueryString = (
  path: string,
  params: RequestOptions['params'] | undefined,
  locale: string,
): string => {
  const allParams: Record<string, string> = { locale };
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        allParams[key] = String(value);
      }
    }
  }
  const searchParams = new URLSearchParams(allParams);
  return (path.includes('?') ? '&' : '?') + searchParams.toString();
};

const handleResponse = async <T>(
  resp: Response,
  requestContext: RequestContext,
  onResponse: ResponseCallback | undefined,
): Promise<T> => {
  const respHeaders = Object.fromEntries(resp.headers.entries());

  let respBody: ApiResponseEnvelope<T>;
  const text = await resp.text();
  if (text.trim() === '') {
    // Bodiless success (204 from a DELETE, 202 from `head :accepted`): there
    // is no envelope to parse and nothing for onResponse to treat as an error.
    respBody = {};
  } else {
    try {
      respBody = JSON.parse(text) as ApiResponseEnvelope<T>;
    } catch {
      if (resp.status >= 500) {
        throw createApiError(
          API_ERROR_TYPES.SERVER,
          'Server error. Please try again later.',
          { status: resp.status, path: requestContext.path },
        );
      }
      respBody = { error: 'Unknown error' };
    }
  }

  onResponse?.(respBody, respHeaders, requestContext);

  if (!resp.ok) {
    const errorType =
      resp.status >= 500 ? API_ERROR_TYPES.SERVER : API_ERROR_TYPES.CLIENT;
    throw createApiError(errorType, respBody.error || 'API request failed', {
      status: resp.status,
      body: respBody,
      response: resp,
      path: requestContext.path,
    });
  }

  return respBody.resp as T;
};

export function createApiClient(config: ApiClientConfig): ApiClient {
  const { apiURL, getToken, getLocale, onResponse } = config;

  const authHeader = (): Record<string, string> => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const request = async <T>(
    method: HttpMethod,
    path: string,
    data: unknown = null,
    options: RequestOptions = {},
  ): Promise<T> => {
    const requestInit: RequestInit = {
      method,
      headers: { ...baseHeaders, ...authHeader(), ...options.headers },
    };

    if (data !== null && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
      requestInit.body = JSON.stringify(data);
    }

    const queryString = buildQueryString(path, options.params, getLocale());
    const url = apiURL + path + queryString;

    let resp: Response;
    try {
      resp = await fetch(url, requestInit);
    } catch (fetchError) {
      throw createApiError(
        API_ERROR_TYPES.NETWORK,
        'Unable to connect to the server. Please check your internet connection.',
        { originalError: fetchError, path },
      );
    }

    const requestContext: RequestContext = { path, data, options };
    return handleResponse<T>(resp, requestContext, onResponse);
  };

  // Blob variants: for binary/non-JSON bodies (CSV downloads, image previews).
  // On success they return the raw Response body as a Blob rather than
  // unwrapping a JSON envelope, so there is no envelope to hand to
  // onResponse — the callback is intentionally not invoked here.
  const requestBlob = async (
    method: 'GET' | 'POST',
    path: string,
    data: unknown = null,
    options: RequestOptions = {},
  ): Promise<Blob> => {
    const requestInit: RequestInit = {
      method,
      headers: {
        ...(data !== null ? { 'Content-Type': 'application/json' } : {}),
        ...authHeader(),
        ...options.headers,
      },
    };
    if (data !== null && method === 'POST') requestInit.body = JSON.stringify(data);

    const url = apiURL + path + buildQueryString(path, options.params, getLocale());
    let resp: Response;
    try {
      resp = await fetch(url, requestInit);
    } catch (fetchError) {
      throw createApiError(
        API_ERROR_TYPES.NETWORK,
        'Unable to connect to the server. Please check your internet connection.',
        { originalError: fetchError, path },
      );
    }
    if (!resp.ok) {
      let body: ApiResponseEnvelope<unknown> | undefined;
      try { body = (await resp.json()) as ApiResponseEnvelope<unknown>; } catch { body = undefined; }
      throw createApiError(
        resp.status >= 500 ? API_ERROR_TYPES.SERVER : API_ERROR_TYPES.CLIENT,
        body?.error || 'API request failed',
        { status: resp.status, body, response: resp, path },
      );
    }
    return resp.blob();
  };

  return {
    get: (path, options = {}) => request('GET', path, null, options),
    post: (path, data, options = {}) => request('POST', path, data, options),
    put: (path, data, options = {}) => request('PUT', path, data, options),
    patch: (path, data, options = {}) => request('PATCH', path, data, options),
    delete: (path, data, options = {}) => request('DELETE', path, data ?? null, options),
    getBlob: (path, options = {}) => requestBlob('GET', path, null, options),
    postBlob: (path, data, options = {}) => requestBlob('POST', path, data ?? null, options),
  };
}
